import { Formik, FormikConfig, useFormikContext } from "formik";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { HandleError } from "../../utils/services";
import { PostCreateProduct, PostEditProduct } from "../../api/post";
import { PopupContext } from "../PopUp/Popup-Context";
import { GetProductDetail } from "../../api/get";
import { ProductDetailResponse } from "../ProductDetails/ProductDetails";

export interface PriceValueProps {
  role_id: number;
  primary_price: number;
  secondary_price: number;
}

export interface NewProductValue {
  name: string;
  primary_unit: string;
  secondary_unit: string;
  relation: string;
  is_damage_reclaimable: boolean;
  tax: number;
  is_price_fixed: boolean;
  batch: number | string;
  price: Array<PriceValueProps>;
  expiry_date: string;
  product_category_id: string;
  product_bar_code: string;
}

export const initialValues: NewProductValue = {
  name: "",
  primary_unit: "",
  secondary_unit: "",
  relation: "",
  expiry_date: "",
  is_damage_reclaimable: false,
  tax: 0,
  is_price_fixed: true,
  batch: 1,
  price: [
    {
      role_id: 4,
      primary_price: 0,
      secondary_price: 0,
    },
  ],
  product_category_id: "",
  product_bar_code: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Please provide product name"),
  primary_unit: Yup.string().required("Please select primary unit"),
  secondary_unit: Yup.string().required("Please select secondary unit"),
  relation: Yup.number().required("Please select relation"),
  // .matches(/^[A-Za-z/, "Not a valid number"),
  batch: Yup.number().required("Please select batch"),
  // expiry_date: Yup.string().required("Please select expiry date"),
  role_id: Yup.array().of(Yup.number().required("Please select a role")),
  is_price_fixed: Yup.boolean(),
  price: Yup.array().when("is_price_fixed", {
    is: true,
    then: Yup.array().of(
      Yup.object().shape({
        role_id: Yup.number().required("Please select role"),
        primary_price: Yup.number().required("Please set primary price"),
        secondary_price: Yup.number().required("Please set secondary price"),
      }),
    ),
  }),
  product_category_id: Yup.string().required("Please select category"),
});

//Function to statically add the product value role wise as needed to api
const priceAddition = (data: PriceValueProps[]) => {
  const roles = [4, 5, 6];
  const existingRoles = new Set(data.map((item) => Number(item.role_id)));

  const missingRoles = roles.filter((role) => !existingRoles.has(role));

  return [
    ...data,
    ...missingRoles.map((role) => ({
      role_id: role,
      secondary_price: 0,
      primary_price: 0,
    })),
  ];
};

export const useProductWrapper = () => {
  const formik = useFormikContext<NewProductValue>();
  if (!formik) {
    throw new Error("useNewCustomerWrapper must be used within a Formik");
  }
  return formik;
};

interface ProductWrapperProps {
  children: React.ReactNode;
  prodId?: number | string;
  mode: "new" | "edit";
  productRefetch?: () => void;
}

export const ProductWrapper: React.FC<ProductWrapperProps> = ({
  children,
  prodId,
  mode,
  productRefetch = () => {},
}) => {
  const { closeModal } = useContext(PopupContext);
  const [prodDetails, setProdDetails] = useState<ProductDetailResponse>();
  let initialValueFromProps: NewProductValue | undefined = undefined;

  if (prodDetails)
    initialValueFromProps = {
      name: prodDetails?.name,
      primary_unit: prodDetails?.primary_unit,
      secondary_unit: prodDetails?.secondary_unit,
      relation: prodDetails?.relation.toString(),
      is_damage_reclaimable: prodDetails?.is_damage_reclaimable,
      tax: Number(prodDetails?.tax),
      is_price_fixed: prodDetails?.is_price_fixed,
      batch: prodDetails?.batch_info?.[0]?.batch_name,
      price: prodDetails?.batch_info?.[0]?.product_price.map((item) => {
        return {
          role_id: item.role_id,
          primary_price: item.primary_price,
          secondary_price: Number(item.secondary_price.toFixed(3)),
        };
      }),
      expiry_date: "",
      product_category_id: "",
      product_bar_code: prodDetails?.product_bar_code,
    };

  const handleSubmit: FormikConfig<NewProductValue>["onSubmit"] = (
    values,
    { setSubmitting },
  ) => {
    setSubmitting(true);
    const newData = { ...values };
    newData.price = priceAddition(values.price);
    const editData = {
      ...values,
      batch: {
        name: values.batch,
        id: prodDetails?.batch_info?.[0]?.batch_id,
      },
      price: values?.price?.map((item) => {
        return {
          ...item,
          product_price_id: prodDetails?.batch_info?.[0]?.product_price.find(
            (itm) => itm.role_id === item.role_id,
          )?.id,
        };
      }),
    };
    const data = mode === "edit" ? editData : newData;

    const apiCall =
      mode === "edit"
        ? PostEditProduct(Number(prodId) || 0, data)
        : PostCreateProduct(data);

    apiCall
      .then(() => {
        toast.success(
          mode === "new"
            ? "New product added successfully"
            : "Product has been updated successfully",
        );
        closeModal();
        productRefetch();
      })
      .catch((err: any) => {
        console.error("Error in product submission:", err);
        const errorMessage = HandleError(err);
        toast.error(errorMessage);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const productDetail = (prodId: number) => {
    GetProductDetail(prodId)
      .then((res) => {
        setProdDetails(res.data.data);
      })
      .catch((err) => {});
  };

  useEffect(() => {
    if (prodId) {
      productDetail(Number(prodId));
    }
  }, [prodId]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValueFromProps || initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {children}
    </Formik>
  );
};
